import {
    computed,
    reactive,
    watch,
    shallowRef,
    onUnmounted,
    onScopeDispose,
    effectScope,
    type Ref,
} from 'vue';
import { usePost } from '@foodics/ui-common';
import { differenceInSeconds } from 'date-fns';
import { sadadApi } from '@/api/sadad';
import type { RefreshSadadBillRequest } from '@/api/sadad';
import type { SadadBill } from '@/types/models';

// ============================================================================
// Configuration
// ============================================================================

export type BillRefreshConfig = {
    /** Interval between batch refresh requests in ms (default: 1000) */
    staggerInterval: number;
    /** Threshold in seconds to consider a bill stale (default: 60) */
    staleThreshold: number;
    /** Maximum number of concurrent refresh requests (default: 3) */
    maxConcurrent: number;
};

const DEFAULT_STALE_THRESHOLD =
    Number(import.meta.env.VITE_BILL_STALE_TIMEOUT_SECONDS) || 60;

const DEFAULT_MAX_CONCURRENT =
    Number(import.meta.env.VITE_BILL_REFRESH_MAX_CONCURRENT) || 3;

const DEFAULT_REFRESH_CONFIG: BillRefreshConfig = {
    staggerInterval: 1000,
    staleThreshold: DEFAULT_STALE_THRESHOLD,
    maxConcurrent: DEFAULT_MAX_CONCURRENT,
};

// ============================================================================
// Types
// ============================================================================

type ActiveRefresh = {
    abort: () => void;
    scope: ReturnType<typeof effectScope>;
};

type OnBillRefreshedCallback = (bill: SadadBill) => void;

// ============================================================================
// Global State
// ============================================================================

const refreshQueue = shallowRef<SadadBill[]>([]);
const activeRefreshes = new Map<string, ActiveRefresh>();
const loadingStates = reactive<Record<string, boolean>>({});
let isProcessing = false;
let processingTimeout: ReturnType<typeof setTimeout> | null = null;
let currentConfig: BillRefreshConfig = { ...DEFAULT_REFRESH_CONFIG };
let onBillRefreshedCallback: OnBillRefreshedCallback | null = null;

// ============================================================================
// Internal Functions
// ============================================================================

/**
 * Process the refresh queue in batches
 * - Starts up to maxConcurrent bills at once
 * - Waits for ALL to complete (regardless of success/failure)
 * - Waits staggerInterval ms
 * - Starts next batch
 */
async function processQueue(): Promise<void> {
    if (isProcessing || refreshQueue.value.length === 0) return;

    // Clear any pending timeout to prevent overlapping batches
    if (processingTimeout) {
        clearTimeout(processingTimeout);
        processingTimeout = null;
    }

    isProcessing = true;

    // Take up to maxConcurrent bills from the queue
    const batchSize = Math.min(
        currentConfig.maxConcurrent,
        refreshQueue.value.length,
    );
    const billsToRefresh = refreshQueue.value.slice(0, batchSize);
    refreshQueue.value = refreshQueue.value.slice(batchSize);

    // Start all refreshes concurrently and wait for all to complete
    await Promise.allSettled(
        billsToRefresh.map((bill) => executeBillRefresh(bill)),
    );

    isProcessing = false;

    // Schedule next batch if there are more in queue
    if (refreshQueue.value.length > 0) {
        processingTimeout = setTimeout(() => {
            processQueue();
        }, currentConfig.staggerInterval);
    }
}

/**
 * Execute the actual bill refresh using usePost
 */
function executeBillRefresh(bill: SadadBill): Promise<void> {
    const billId = bill.id;
    const scope = effectScope();
    loadingStates[billId] = true;

    return new Promise<void>((resolve) => {
        scope.run(async () => {
            const { execute, abort } = usePost<
                RefreshSadadBillRequest['payload'],
                RefreshSadadBillRequest['response']
            >(sadadApi.refreshBill(billId));

            activeRefreshes.set(billId, { abort, scope });

            try {
                const { data, error } = await execute();

                if (error) {
                    console.error(`Failed to refresh bill ${billId}:`, error);
                } else if (data?.data) {
                    onBillRefreshedCallback?.(data.data);
                }
            } catch (error) {
                if ((error as Error)?.name !== 'AbortError') {
                    console.error(`Failed to refresh bill ${billId}:`, error);
                }
            } finally {
                activeRefreshes.delete(billId);
                loadingStates[billId] = false;
                scope.stop();
                resolve();
            }
        });
    });
}

// ============================================================================
// Public API
// ============================================================================

/**
 * Set callback for when a bill is refreshed
 */
export function onBillRefreshed(callback: OnBillRefreshedCallback): void {
    onBillRefreshedCallback = callback;
}

/**
 * Check if a bill is stale and needs refreshing
 */
export function isBillStale(
    bill: SadadBill,
    thresholdSeconds = currentConfig.staleThreshold,
): boolean {
    if (!bill.last_refreshed_at) return true;
    return (
        differenceInSeconds(new Date(), new Date(bill.last_refreshed_at)) >
        thresholdSeconds
    );
}

/**
 * Check if a bill is currently being refreshed
 */
export function isBillRefreshing(billId: string): boolean {
    return loadingStates[billId] ?? false;
}

/**
 * Get reactive loading state for a bill
 */
export function useBillRefreshState(billId: string) {
    return computed(() => isBillRefreshing(billId));
}

/**
 * Queue a single bill for refresh (if stale and not already queued/refreshing)
 */
export function queueBillRefresh(bill: SadadBill): boolean {
    // Skip if bill is not stale
    if (!isBillStale(bill)) return false;

    // Skip if already queued or refreshing
    if (isBillRefreshing(bill.id)) return false;
    if (refreshQueue.value.some((b) => b.id === bill.id)) return false;

    refreshQueue.value = [...refreshQueue.value, bill];
    return true;
}

/**
 * Queue multiple bills for refresh (filters stale bills automatically)
 */
export function queueBillsRefresh(bills: SadadBill[]): void {
    // Add all stale bills to queue first
    bills.forEach(queueBillRefresh);

    // Then start processing (all bills already in queue)
    processQueue();
}

/**
 * Abort a specific bill refresh
 */
export function abortBillRefresh(billId: string): void {
    const activeRefresh = activeRefreshes.get(billId);
    if (activeRefresh) {
        activeRefresh.abort();
        activeRefresh.scope.stop();
        activeRefreshes.delete(billId);
    }

    refreshQueue.value = refreshQueue.value.filter((b) => b.id !== billId);
    loadingStates[billId] = false;
}

/**
 * Abort all ongoing and queued refreshes
 */
export function abortAllBillRefreshes(): void {
    // Clear processing timeout
    if (processingTimeout) {
        clearTimeout(processingTimeout);
        processingTimeout = null;
    }

    activeRefreshes.forEach((activeRefresh) => {
        activeRefresh.abort();
        activeRefresh.scope.stop();
    });
    activeRefreshes.clear();

    refreshQueue.value = [];

    Object.keys(loadingStates).forEach((key) => {
        loadingStates[key] = false;
    });

    isProcessing = false;
}

/**
 * Update refresh configuration
 */
export function configureRefresh(config: Partial<BillRefreshConfig>): void {
    currentConfig = { ...currentConfig, ...config };
}

/**
 * Get current refresh configuration
 */
export function getRefreshConfig(): BillRefreshConfig {
    return { ...currentConfig };
}

// ============================================================================
// Composables
// ============================================================================

/**
 * Composable for managing bill refresh in listing screens.
 * Automatically queues stale bills and aborts on scope dispose.
 */
export function useBillRefreshManager(bills: Ref<SadadBill[]>) {
    watch(
        bills,
        (newBills) => {
            queueBillsRefresh(newBills);
        },
        { immediate: true },
    );

    onScopeDispose(() => {
        abortAllBillRefreshes();
    });

    return {
        queueRefresh: queueBillRefresh,
        queueRefreshAll: () => queueBillsRefresh(bills.value),
        abortAll: abortAllBillRefreshes,
        isRefreshing: isBillRefreshing,
        isBillStale,
    };
}

/**
 * Composable to auto-refresh a single bill if stale.
 * Use for individual bill detail views (NOT for list items).
 * For list items, use useBillLoadingState() and useBillRefreshManager() at list level.
 */
export function useAutoRefreshBill(bill: SadadBill) {
    const isRefreshing = computed(() => isBillRefreshing(bill.id));

    if (isBillStale(bill)) {
        queueBillRefresh(bill);
        processQueue(); // Start processing for single bill
    }

    onUnmounted(() => {
        abortBillRefresh(bill.id);
    });

    return {
        isRefreshing,
        abort: () => abortBillRefresh(bill.id),
    };
}

/**
 * Composable to get loading state for a bill in a list.
 * Does NOT auto-queue - use with useBillRefreshManager() at list level.
 */
export function useBillLoadingState(billId: string) {
    return {
        isRefreshing: computed(() => isBillRefreshing(billId)),
    };
}
